import React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode; 
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, loading, authInitialized, isConfigured } = useAuth();
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = useState(false);
  
  // Add timeout protection for loading state
  useEffect(() => {
    // Prevent undefined errors by ensuring proper state initialization
    if (!authInitialized && !loading) {
      console.warn('PrivateRoute: Inconsistent state detected, forcing re-initialization');
    }
  }, [authInitialized, loading]);
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading || !authInitialized) {
        setTimeoutReached(true);
      }
    }, 2000); // 2 second timeout

    return () => clearTimeout(timeout);
  }, [loading, authInitialized]);
  
  // Show loading state
  if ((loading || !authInitialized) && !timeoutReached) { 
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="text-center p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900">Loading your account...</h1>
          <p className="text-gray-600 mt-2">
            If this takes too long, try refreshing the page
          </p>
          <div className="mt-4 text-xs text-gray-500">
            <p>Status: {loading ? 'Loading' : 'Not Loading'}, Auth Initialized: {authInitialized ? 'Yes' : 'No'}</p>
            <p>Supabase Configured: {isConfigured ? 'Yes' : 'No'}</p>
            {timeoutReached && (
              <p className="text-red-500 font-bold">Timeout reached - please refresh if needed</p>
            )}
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // If timeout reached but still no auth, show error
  if (timeoutReached && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Timeout</h1>
          <p className="text-gray-600 mb-6">
            The authentication process took too long. Please try refreshing the page or logging in again.
          </p>
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 mr-3"
            >
              Refresh Page
            </button>
            <Link to="/login" className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Show configuration error if Supabase is not configured
  if (!isConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-amber-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Configuration Error</h1>
          <p className="text-gray-600 mb-6">
            The application is not properly configured. Please check your environment variables.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
  
  // Not logged in - redirect to login
  if (!user) {
    // Check if we have a stale token in localStorage
    const hasStaleToken = Object.keys(localStorage).some(key =>
      key.includes('supabase') || key.includes('sb-')
    );

    if (hasStaleToken) {
      // Clear only Supabase-related items
      localStorage.removeItem('career_lens_admin_status');
      localStorage.removeItem('career_lens_admin_email');
      localStorage.removeItem('career_lens_admin_status_timestamp');
      sessionStorage.removeItem('admin_login_success');

      // Clear any Supabase tokens
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          localStorage.removeItem(key);
        }
      }

      // Redirect to login with stale parameter
      return <Navigate to="/login?stale=true" state={{ from: location }} replace />;
    } else {
      // Preserve the attempted URL so we can redirect after login
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }

  // User is authenticated, render the children
  return <>{children}</>;
};

export default PrivateRoute;